import React, { useEffect, useState } from "react";
import client, { apiErr } from "../lib/api";
import { Button, Input, Card, Modal, Badge, Select } from "../components/ui";
import { APP_FULL } from "../i18n";
import { UserPlus, KeyRound, Ban, CheckCircle2, Trash2, Link2, Users, Search, Send, ShieldAlert, Unlock, FileCheck, Download, MapPin, Check, CheckCheck } from "lucide-react";

export default function Runners() {
  const [runners, setRunners] = useState([]);
  const [licenses, setLicenses] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", phone: "", password: "", send_invite: true });
  const [msg, setMsg] = useState("");
  const [manageFor, setManageFor] = useState(null);
  const [pwFor, setPwFor] = useState(null);
  const [newPw, setNewPw] = useState("");
  const [q, setQ] = useState("");
  const [sortBy, setSortBy] = useState("name");
  const [complianceFilter, setComplianceFilter] = useState("");
  const [signed, setSigned] = useState({});
  const [compliance, setCompliance] = useState(null);

  const load = () => {
    client.get("/admin/runners").then((r) => setRunners(r.data)).catch(() => {});
    client.get("/licenses").then((r) => setLicenses(r.data)).catch(() => {});
    client.get("/admin/signed-agreements").then((r) => {
      const m = {};
      (r.data.items || []).forEach((s) => { if (!m[s.user_id]) m[s.user_id] = s; });
      setSigned(m);
    }).catch(() => {});
    client.get("/admin/compliance-summary").then((r) => setCompliance(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const viewSigned = async (rec) => {
    try {
      const res = await client.get(`/agreements/${rec.id}.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (e) { setMsg(apiErr(e)); }
  };

  const exportSigned = async () => {
    try {
      const res = await client.get("/admin/signed-agreements.csv", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `signed_agreements_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { setMsg(apiErr(e)); }
  };

  const remindPending = async () => {
    if (!complianceFilter) return;
    const label = complianceFilter === "unsigned" ? "awaiting eSignature" : "with a pending policy acknowledgment";
    if (!window.confirm(`Send an email/SMS reminder to all runners ${label}?`)) return;
    setMsg("Sending reminders…");
    try {
      const { data } = await client.post("/admin/runners/remind", { scope: complianceFilter, origin_url: window.location.origin });
      setMsg(`Reminder sent to ${data.reminded} of ${data.targets} runner(s) ${label}.`);
    } catch (e) { setMsg(apiErr(e)); }
  };

  const create = async () => {
    setMsg("");
    try {
      const { data } = await client.post("/admin/runners", { ...form, origin_url: window.location.origin });
      setOpen(false); setForm({ email: "", name: "", phone: "", password: "", send_invite: true }); load();
      const st = data.invite ? data.invite.map((d) => `${d.channel}:${d.status}`).join(", ") : "no invite sent";
      setMsg(`Runner created. Invite → ${st}.`);
    } catch (e) { setMsg(apiErr(e)); }
  };
  const toggle = async (r) => { await client.post(`/admin/runners/${r.id}/set-disabled`, { disabled: !r.disabled }); load(); };
  const unblock = async (r) => { await client.post(`/admin/runners/${r.id}/unblock`); setMsg(`Access restored for ${r.email}.`); load(); };
  const del = async (r) => { if (window.confirm(`Delete runner ${r.email}? Their licenses will be unassigned.`)) { await client.delete(`/admin/runners/${r.id}`); load(); } };
  const resetPw = async () => { await client.post(`/admin/runners/${pwFor.id}/reset-password`, { password: newPw }); setPwFor(null); setNewPw(""); setMsg("Password reset."); };
  const resendInvite = async (r) => {
    setMsg("");
    try {
      const { data } = await client.post(`/admin/runners/${r.id}/invite`, { origin_url: window.location.origin });
      const st = (data.invite || []).map((d) => `${d.channel}:${d.status}`).join(", ");
      setMsg(`Secure setup link sent to ${r.email} → ${st}. The link expires in 72 hours.`);
    } catch (e) { setMsg(apiErr(e)); }
  };
  const resendAll = async () => {
    const pend = runners.filter((r) => r.must_change_password && !r.disabled);
    if (pend.length === 0) return;
    setMsg(`Resending ${pend.length} invite(s)…`);
    let ok = 0;
    for (const r of pend) {
      try { await client.post(`/admin/runners/${r.id}/invite`, { origin_url: window.location.origin }); ok++; } catch (e) { /* skip */ }
    }
    setMsg(`Resent setup links to ${ok} of ${pend.length} pending runner(s). Links expire in 72 hours.`);
  };
  const assign = async (licId, runnerId) => { await client.post(`/licenses/${licId}/assign`, { runner_id: runnerId || null }); load(); };

  const assignedTo = (rid) => licenses.filter((l) => l.assigned_to === rid);
  const unassigned = licenses.filter((l) => !l.assigned_to);

  const isAckPending = (r) => !!compliance?.policy_ack_active
    && !r.disabled && (!r.policy_ack_at || (compliance.issued_at && r.policy_ack_at < compliance.issued_at));
  const isUnsigned = (r) => !r.disabled && !r.agreement_signed_at;

  const firstName = (n) => String(n || "").trim().split(/\s+/)[0] || "";
  const lastName = (n) => { const p = String(n || "").trim().split(/\s+/); return p.length > 1 ? p[p.length - 1] : ""; };
  const statusRank = (r) => (r.blocked ? 0 : r.disabled ? 1 : r.must_change_password ? 2 : 3);
  const cmp = (a, b) => String(a || "").localeCompare(String(b || ""));
  const sorters = {
    name: (a, b) => cmp(a.name, b.name),
    first_name: (a, b) => cmp(firstName(a.name), firstName(b.name)),
    last_name: (a, b) => cmp(lastName(a.name), lastName(b.name)),
    email: (a, b) => cmp(a.email, b.email),
    city: (a, b) => cmp(a.city, b.city) || cmp(a.name, b.name),
    country: (a, b) => cmp(a.country, b.country) || cmp(a.name, b.name),
    status: (a, b) => statusRank(a) - statusRank(b) || cmp(a.name, b.name),
    apps: (a, b) => (b.assigned || 0) - (a.assigned || 0) || cmp(a.name, b.name),
    newest: (a, b) => cmp(b.created_at, a.created_at),
    oldest: (a, b) => cmp(a.created_at, b.created_at),
    grace_opted: (a, b) => ((a.grace?.opted ? 0 : 1) - (b.grace?.opted ? 0 : 1)) || cmp(a.name, b.name),
    grace_warned: (a, b) => ((a.grace?.warned ? 0 : 1) - (b.grace?.warned ? 0 : 1)) || cmp(a.name, b.name),
    grace_suspended: (a, b) => ((a.grace?.suspended ? 0 : 1) - (b.grace?.suspended ? 0 : 1)) || cmp(a.name, b.name),
    grace_reinstated: (a, b) => ((a.grace?.reinstated ? 0 : 1) - (b.grace?.reinstated ? 0 : 1)) || cmp(a.name, b.name),
    name_mismatch: (a, b) => ((a.name_match?.status === "mismatch" ? 0 : 1) - (b.name_match?.status === "mismatch" ? 0 : 1)) || cmp(a.name, b.name),
  };

  const shown = runners
    .filter((r) => {
      if (complianceFilter === "ack_pending" && !isAckPending(r)) return false;
      if (complianceFilter === "unsigned" && !isUnsigned(r)) return false;
      if (!q) return true;
      const s = q.toLowerCase();
      return [r.name, r.email, r.phone, r.city, r.country].some((v) => String(v || "").toLowerCase().includes(s));
    })
    .sort(sorters[sortBy] || sorters.name);
  const pendingCount = runners.filter((r) => r.must_change_password && !r.disabled).length;
  const toggleFilter = (f) => setComplianceFilter((cur) => (cur === f ? "" : f));

  return (
    <div className="space-y-6 fadeup">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="font-head text-2xl sm:text-3xl flex items-center gap-2"><Users size={24} /> Runner Accounts</h1>
        {pendingCount > 0 && <Badge color="warning" data-testid="pending-count">{pendingCount} pending setup</Badge>}
        <div className="ms-auto flex gap-2">
          {pendingCount > 0 && <Button variant="subtle" onClick={resendAll} data-testid="resend-all"><Send size={16} /> Resend all invites</Button>}
          {Object.keys(signed).length > 0 && <Button variant="subtle" onClick={exportSigned} data-testid="export-signed-csv"><Download size={16} /> Export agreements CSV</Button>}
          <Button onClick={() => setOpen(true)} data-testid="add-runner"><UserPlus size={16} /> New runner</Button>
        </div>
      </div>

      {msg && <Card className="border-[var(--primary)]/40 text-sm" data-testid="runner-msg">{msg}</Card>}

      {compliance && compliance.runners_total > 0 && (
        <Card className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm" data-testid="compliance-summary">
          <span className="inline-flex items-center gap-1.5 font-medium">
            <FileCheck size={16} className="text-emerald-600 dark:text-emerald-400" />
            <span data-testid="compliance-signed">{compliance.signed} of {compliance.runners_total}</span> runners signed
          </span>
          {compliance.unsigned > 0 && (
            <button type="button" onClick={() => toggleFilter("unsigned")} data-testid="compliance-unsigned"
              className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 transition-colors hover:bg-amber-500/10 ${complianceFilter === "unsigned" ? "bg-amber-500/15 font-medium" : "opacity-80"}`}
              title="Show only runners awaiting eSignature">
              <ShieldAlert size={16} className="text-amber-600 dark:text-amber-400" />
              {compliance.unsigned} awaiting eSignature
            </button>
          )}
          {compliance.policy_ack_active ? (
            compliance.ack_pending > 0 ? (
              <button type="button" onClick={() => toggleFilter("ack_pending")} data-testid="compliance-ack"
                className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 transition-colors hover:bg-amber-500/10 ${complianceFilter === "ack_pending" ? "bg-amber-500/15 font-medium" : ""}`}
                title="Show only runners who haven't acknowledged the policy update">
                <ShieldAlert size={16} className="text-amber-600 dark:text-amber-400" />
                {compliance.ack_pending} pending policy acknowledgment
              </button>
            ) : (
              <span className="inline-flex items-center gap-1.5" data-testid="compliance-ack">
                <ShieldAlert size={16} className="text-emerald-600 dark:text-emerald-400" />
                All caught up on policy acknowledgments
              </span>
            )
          ) : (
            <span className="inline-flex items-center gap-1.5 opacity-50" data-testid="compliance-ack-off">
              No policy acknowledgment prompt active
            </span>
          )}
          {complianceFilter && (
            <div className="ms-auto flex items-center gap-3">
              <button type="button" onClick={remindPending} data-testid="compliance-remind"
                className="inline-flex items-center gap-1.5 rounded-full bg-[var(--primary)] px-3 py-1 text-xs font-semibold text-white hover:bg-[var(--primary-hover)]">
                <Send size={13} /> Remind these runners
              </button>
              <button type="button" onClick={() => setComplianceFilter("")} data-testid="compliance-clear-filter"
                className="inline-flex items-center gap-1 text-xs font-medium text-[var(--primary)] hover:underline">
                Clear filter ✕
              </button>
            </div>
          )}
        </Card>
      )}

      <div className="flex flex-wrap items-end gap-3">
        <div className="relative max-w-md flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 opacity-50" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, email or phone..."
            className="w-full rounded-md border border-black/15 dark:border-white/15 bg-white/60 dark:bg-black/30 py-2 ps-9 pe-3 text-sm" data-testid="runner-search" />
        </div>
        <div className="w-52"><Select label="Sort by" value={sortBy} onChange={(e) => setSortBy(e.target.value)} data-testid="runner-sort">
          <option value="name">Name</option>
          <option value="first_name">First name</option>
          <option value="last_name">Last name</option>
          <option value="email">Email</option>
          <option value="city">City</option>
          <option value="country">Country</option>
          <option value="apps">Apps held (most first)</option>
          <option value="status">Status (issues first)</option>
          <option value="newest">Account age (newest)</option>
          <option value="oldest">Account age (oldest)</option>
          <option value="grace_opted">Grace: opted first</option>
          <option value="grace_warned">Grace: unpaid at 15 days first</option>
          <option value="grace_suspended">Grace: suspended first</option>
          <option value="grace_reinstated">Grace: reinstated first</option>
          <option value="name_mismatch">Name mismatches first</option>
        </Select></div>
      </div>

      <div className="overflow-x-auto">
      {shown.length === 0 ? (
        <Card className="text-center opacity-60">{runners.length === 0 ? "No runner accounts yet. Create one to delegate instance activation." : "No runners match your search."}</Card>
      ) : (
      <div className="min-w-[1120px] overflow-hidden rounded-lg border border-black/20 dark:border-white/20 divide-y divide-black/15 dark:divide-white/15">
        {shown.map((r) => (
          <div key={r.id} className="flex items-stretch" data-testid={`runner-${r.id}`}>
            {/* Column 1 — identity & status */}
            <div className="flex-1 min-w-0 flex flex-col justify-center gap-1 p-3">
              <div className="flex flex-wrap items-center gap-2">
                <KeyRound size={16} className="text-[var(--primary)]" />
                <span className="font-head font-medium">{r.name}</span>
                {r.disabled ? <Badge color="danger">disabled</Badge> : <Badge color="success">active</Badge>}
                {r.blocked && <Badge color="danger" data-testid={`blocked-${r.id}`}><ShieldAlert size={11} className="inline" /> blocked</Badge>}
                {r.must_change_password && <Badge color="warning" data-testid={`pending-${r.id}`}>pending setup</Badge>}
                <Badge color="info">{r.assigned} instance{r.assigned === 1 ? "" : "s"}</Badge>
              </div>
              <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs opacity-70">
                <span>{r.email}</span>
                {r.phone && <span>{r.phone}</span>}
                {(r.city || r.country) && <span data-testid={`runner-meta-${r.id}`}>📍 {[r.city, r.country].filter(Boolean).join(", ")}</span>}
                {(r.apps || []).map((a) => (
                  <span key={a} className="rounded-full bg-black/10 px-1.5 capitalize dark:bg-white/10">{String(a).replace(/_/g, " ")}</span>
                ))}
              </div>
            </div>
            {/* Column 2 — agreement + Grace */}
            <div className="flex-1 min-w-0 flex items-center justify-between gap-2 border-l border-black/20 p-3 dark:border-white/20">
              {signed[r.id] ? (
                <button type="button" onClick={() => viewSigned(signed[r.id])} data-testid={`signed-${r.id}`}
                  className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 px-2 py-0.5 text-xs font-medium text-emerald-700 hover:bg-emerald-500/25 dark:text-emerald-400"
                  title="View the executed agreement PDF">
                  <FileCheck size={12} /> Signed {(signed[r.id].signed_at || "").slice(0, 10)} · {(signed[r.id].agreement_type || "").toUpperCase()}
                </button>
              ) : (
                <Badge color="warning" data-testid={`unsigned-${r.id}`}>agreement not signed</Badge>
              )}
              {(() => {
                const nm = r.name_match || { status: "pending" };
                const map = {
                  match: { cls: "border-emerald-500/50 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400", label: "Name match", icon: <Check size={13} /> },
                  partial: { cls: "border-emerald-500/40 bg-emerald-500/[0.06] text-emerald-700 dark:text-emerald-400", label: "Name ok", icon: <Check size={13} /> },
                  mismatch: { cls: "border-[#DA1E28]/50 bg-[#DA1E28]/10 text-[#DA1E28] animate-pulse", label: "Name mismatch", icon: <ShieldAlert size={13} /> },
                  pending: { cls: "border-black/20 bg-black/[0.02] opacity-55 dark:border-white/20 dark:bg-white/[0.04]", label: "Name —", icon: null },
                }[nm.status] || {};
                const tip = nm.status === "mismatch"
                  ? `Name mismatch — card: "${nm.card || "—"}" · agreement: "${nm.sign || "—"}" · registrant: "${nm.reg || "—"}"`
                  : nm.status === "pending" ? "Name match pending — awaiting card / agreement / registration name"
                  : `Names match — card / agreement / registrant`;
                return (
                  <span className={`inline-flex items-center gap-1 rounded-md border-2 border-dashed px-2 py-1 text-[11px] font-medium ${map.cls}`}
                    data-testid={`reserve1-${r.id}`} title={tip}>
                    {map.icon}<span className="text-[10px] font-semibold uppercase tracking-wide">{map.label}</span>
                  </span>
                );
              })()}
              <span className="inline-flex items-center gap-1.5 rounded-md border-2 border-dashed border-black/20 bg-black/[0.02] px-2 py-1 text-[11px] opacity-55 dark:border-white/20 dark:bg-white/[0.04]" data-testid={`reserve2-${r.id}`} title="Reserved slot for a future status badge">
                <span className="text-[10px] font-semibold uppercase tracking-wide">Reserve 2</span>
              </span>
              <span className="inline-flex items-center gap-1.5 rounded-md border-2 border-dashed border-black/25 bg-black/[0.03] px-2 py-1 dark:border-white/25 dark:bg-white/[0.05]"
                data-testid={`grace-${r.id}`} title="30-day grace period status">
                <span className="text-[10px] font-semibold uppercase tracking-wide opacity-55">Grace</span>
                {(!r.grace || r.grace.status === "none") ? (
                  <span className="text-[11px] opacity-45" data-testid={`grace-none-${r.id}`}>not opted</span>
                ) : (<>
                  {r.grace.opted && <span className="h-2.5 w-2.5 rounded-full bg-emerald-500" title="Opted for the 30-day grace period" data-testid={`grace-green-${r.id}`} />}
                  {r.grace.warned && <span className="h-2.5 w-2.5 rounded-full bg-amber-400 animate-pulse" title="Unpaid at 15 days — email + SMS reminder sent" data-testid={`grace-yellow-${r.id}`} />}
                  {r.grace.suspended && <span className="h-2.5 w-2.5 rounded-full bg-[#DA1E28] animate-pulse" title="Suspended — grace period elapsed unpaid" data-testid={`grace-red-${r.id}`} />}
                  {r.grace.paid_ontime && <Check size={14} className="text-emerald-600 dark:text-emerald-400" title="Paid before suspension" data-testid={`grace-check1-${r.id}`} />}
                  {r.grace.reinstated && <CheckCheck size={15} className="text-emerald-600 dark:text-emerald-400" title="Reinstated by payment after suspension" data-testid={`grace-check2-${r.id}`} />}
                </>)}
              </span>
            </div>
            {/* Column 3 — actions */}
            <div className="flex-1 min-w-0 flex flex-wrap items-center gap-2 border-l border-black/20 p-3 dark:border-white/20" data-testid={`runner-actions-${r.id}`}>
              <Button variant="subtle" onClick={() => setManageFor(r)} data-testid={`manage-${r.id}`}><Link2 size={15} /> Instances</Button>
              <Button variant="subtle" onClick={() => resendInvite(r)} data-testid={`invite-${r.id}`}><Send size={15} /> Resend invite</Button>
              <Button variant="subtle" onClick={() => { setPwFor(r); setNewPw(""); }} data-testid={`pw-${r.id}`}><KeyRound size={15} /> Reset password</Button>
              <Button variant="ghost" onClick={() => toggle(r)} data-testid={`toggle-${r.id}`}>{r.disabled ? <><CheckCircle2 size={15} /> Enable</> : <><Ban size={15} /> Disable</>}</Button>
              {r.blocked && <Button variant="subtle" onClick={() => unblock(r)} data-testid={`unblock-${r.id}`}><Unlock size={15} /> Restore access</Button>}
              <Button variant="danger" onClick={() => del(r)} data-testid={`del-runner-${r.id}`}><Trash2 size={15} /></Button>
            </div>
          </div>
        ))}
      </div>
      )}
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title="Create runner account" testid="runner-modal">
        <div className="space-y-3">
          <Input label="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="runner-name" />
          <Input label="Email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="runner-email" />
          <Input label="Phone (for SMS invite, optional)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="runner-phone" />
          <Input label="Set a password manually (optional — leave blank to use the secure link)" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="runner-password" />
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.send_invite} onChange={(e) => setForm({ ...form, send_invite: e.target.checked })} data-testid="runner-invite" className="h-4 w-4" />
            Email (and SMS if phone given) a secure one-time setup link (expires in 72h)
          </label>
          <div className="flex justify-end gap-2 pt-2"><Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={create} data-testid="submit-runner">Create</Button></div>
        </div>
      </Modal>

      <Modal open={!!manageFor} onClose={() => setManageFor(null)} title={`Instances — ${manageFor?.name || ""}`} testid="manage-modal">
        {manageFor && (
          <div className="space-y-4">
            <div>
              <div className="mb-1 text-xs font-medium uppercase tracking-wide opacity-60">Assigned to this runner</div>
              {assignedTo(manageFor.id).length === 0 && <div className="text-sm opacity-50">None assigned.</div>}
              {assignedTo(manageFor.id).map((l) => (
                <div key={l.id} className="flex items-center justify-between border-b border-black/5 dark:border-white/5 py-2 text-sm" data-testid={`assigned-${l.id}`}>
                  <span>{APP_FULL[l.app_type] || l.app_type} · {l.first_ten}··</span>
                  <Button variant="ghost" onClick={() => assign(l.id, null)} data-testid={`unassign-${l.id}`}>Unassign</Button>
                </div>
              ))}
            </div>
            <div>
              <div className="mb-1 text-xs font-medium uppercase tracking-wide opacity-60">Available to assign</div>
              {unassigned.length === 0 && <div className="text-sm opacity-50">No unassigned instances. Generate more in the Master Enabler.</div>}
              {unassigned.map((l) => (
                <div key={l.id} className="flex items-center justify-between border-b border-black/5 dark:border-white/5 py-2 text-sm" data-testid={`avail-${l.id}`}>
                  <span>{APP_FULL[l.app_type] || l.app_type} · {l.first_ten}··</span>
                  <Button variant="subtle" onClick={() => assign(l.id, manageFor.id)} data-testid={`assign-${l.id}`}>Assign</Button>
                </div>
              ))}
            </div>
          </div>
        )}
      </Modal>

      <Modal open={!!pwFor} onClose={() => setPwFor(null)} title={`Reset password — ${pwFor?.email || ""}`} testid="pw-modal">
        <div className="space-y-3">
          <Input label="New password" value={newPw} onChange={(e) => setNewPw(e.target.value)} data-testid="pw-input" />
          <div className="flex justify-end gap-2"><Button variant="ghost" onClick={() => setPwFor(null)}>Cancel</Button><Button onClick={resetPw} data-testid="submit-pw">Reset</Button></div>
        </div>
      </Modal>
    </div>
  );
}
